import { _js } from '@ifixit/utils/localize';
import { useState, useCallback } from 'react';
import { color, space } from '@ifixit/primitives';
import { FaIcon } from '@ifixit/ui/components/icon';
import styled from 'styled-components';
import Cookies from 'universal-cookie';
import Documents, { DisplayDocument, CellViewType } from './documents';
import initializeComponent from 'Shared/react-initialize';

type ViewButtonProps = {
   $selected: boolean;
};

const Header = styled.h2`
   display: flex;
   align-items: center;
   justify-content: space-between;
`;

const ViewButton = styled.button<ViewButtonProps>`
   display: flex;
   align-items: center;
   cursor: pointer;
   border: none;
   padding: 0;
   color: ${props => (props.$selected ? color.gray[800] : color.gray[400])};
   background-color: transparent;
   transition: color 0.1s;

   &:first-child {
      margin-right: ${space[4]};
   }

   &:hover,
   &:focus {
      outline: none;
      color: ${color.gray[600]};
   }

   &:active {
      color: ${color.gray[800]};
   }
`;

const cookies = new Cookies();
const DOCUMENT_VIEW_KEY = 'documentView';
const DOCUMENT_VIEW_SWITCH = 12;

const initialView = (numDocuments: any) => {
   const documentView = cookies.get(DOCUMENT_VIEW_KEY);
   return (
      documentView ||
      ((numDocuments <= DOCUMENT_VIEW_SWITCH
         ? CellViewType.Gallery
         : CellViewType.List) as CellViewType)
   );
};

const DocumentsSection = ({
   documents,
   anchorId,
}: {
   documents: DisplayDocument[];
   anchorId: string;
}) => {
   const [view, setView] = useState(initialView(documents.length));

   const setViewAndCookie = useCallback(
      (view: any) => {
         setView(view);
         cookies.set(DOCUMENT_VIEW_KEY, view);
      },
      [setView]
   );

   const sectionName = _js('Documents');
   return (
      <div>
         <Header className="group relative">
            {/* Out of flow so space-between still sees two children. */}
            <a
               href={`#${anchorId}`}
               title={_js('Link to this section')}
               aria-hidden="true"
               tabIndex={-1}
               className="absolute -left-5 top-1/2 -translate-y-1/2 text-gray-500 opacity-0 group-hover:opacity-100"
            >
               <FaIcon name="link" style="regular" className="h-3.5 w-auto aspect-[640/512]" />
            </a>
            {sectionName}
            <span className="flex self-stretch">
               <ViewButton
                  $selected={view === CellViewType.List}
                  onClick={() => setViewAndCookie(CellViewType.List)}
                  title={_js('List View')}
               >
                  <FaIcon name="list" style="regular" className="size-3.5" />
               </ViewButton>
               <ViewButton
                  $selected={view === CellViewType.Gallery}
                  onClick={() => setViewAndCookie(CellViewType.Gallery)}
                  title={_js('Gallery View')}
               >
                  <FaIcon name="grid-2" style="regular" className="size-3.5" />
               </ViewButton>
            </span>
         </Header>
         <Documents documents={documents} view={view} />
      </div>
   );
};

initializeComponent('DocumentsSection', DocumentsSection);
